/**
 * Retry Backoff System - localStorage utilities
 *
 * Tracks consecutive Apollo Client network failures and implements
 * an automatic backoff mechanism to reduce system load during incidents.
 *
 * Strategy:
 * - Failure count is kept in memory (resets on page load)
 * - Only backoffUntil timestamp is persisted to localStorage
 * - During real incidents, count hits threshold in seconds due to frequent requests
 *
 * Circuit Breaker Pattern:
 * - Trigger backoff quickly after N consecutive failures (protect during incidents)
 * - Clear backoff slowly after M consecutive successes (ensure stability before re-enabling)
 * - This prevents "flapping" behavior during partial outages or gradual recovery
 *
 * Why require consecutive successes to clear backoff?
 * 1. Asymmetric recovery prevents yo-yo behavior during incidents
 *    - Systems often show intermittent success during recovery
 *    - One successful request doesn't mean the incident is over
 * 2. Mirrors circuit breaker patterns in distributed systems
 *    - Open (backoff): Block retries after failures
 *    - Half-open (testing): Require N successful trials before fully recovering
 *    - Closed (normal): Allow retries
 * 3. Real incidents show inconsistent behavior during recovery
 *    - Database failovers with intermittent successes
 *    - Load balancer changes making some requests work
 *    - Rolling restarts causing variable behavior
 * 4. We don't want to re-enable aggressive retries until system is truly stable
 *
 * Related: CORE-3118 - Feature to disable auto retry during major incidents
 */

const BACKOFF_DURATION_MS = 10 * 60 * 1000 // 10 minutes
const FAILURE_THRESHOLD = 10 // consecutive failures before triggering backoff

// Success threshold is 30% of failure threshold to prevent flapping
// If FAILURE_THRESHOLD changes, this automatically scales
const SUCCESS_THRESHOLD = Math.ceil(FAILURE_THRESHOLD * 0.3) // consecutive successes to clear backoff

const STORAGE_KEY = 'apollo-retry-backoff-until'

// In-memory counters (reset on page load)
let failureCount = 0
let successCount = 0

export interface BackoffState {
  failureCount: number
  successCount: number
  backoffUntil: number | null
  isInBackoff: boolean
}

/**
 * Safely get value from localStorage with fallback
 */
function getStorageItem(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    // local storage unavailable, do nothing
    return null
  }
}

/**
 * Safely set value in localStorage
 */
function setStorageItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // local storage unavailable, do nothing
  }
}

/**
 * Safely remove value from localStorage
 */
function removeStorageItem(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // local storage unavailable, do nothing
  }
}

/**
 * Get current backoff state
 */
export function getBackoffState(): BackoffState {
  const backoffUntilStr = getStorageItem(STORAGE_KEY)
  const backoffUntil = backoffUntilStr ? parseInt(backoffUntilStr, 10) : null

  const now = Date.now()
  const currentlyInBackoff = backoffUntil !== null && now < backoffUntil

  return {
    failureCount,
    successCount,
    backoffUntil,
    isInBackoff: currentlyInBackoff,
  }
}

/**
 * Record a network failure and potentially trigger backoff
 * Returns true if backoff was triggered
 */
export function recordFailure(): boolean {
  const state = getBackoffState()

  // Reset success streak on any failure
  successCount = 0
  failureCount += 1

  const now = Date.now()

  // Trigger backoff if threshold reached
  if (failureCount >= FAILURE_THRESHOLD && !state.isInBackoff) {
    const backoffUntil = now + BACKOFF_DURATION_MS
    setStorageItem(STORAGE_KEY, backoffUntil.toString())

    return true
  }

  return false
}

/**
 * Record a successful request
 *
 * Requires SUCCESS_THRESHOLD consecutive successes to clear backoff.
 * This prevents "flapping" during partial outages where some requests
 * succeed intermittently while the system is still struggling.
 */
export function recordSuccess(): void {
  // Increment success count
  successCount += 1

  // Only clear backoff after N consecutive successes
  // This ensures the system is truly stable before re-enabling retries
  if (successCount >= SUCCESS_THRESHOLD) {
    failureCount = 0
    successCount = 0
    removeStorageItem(STORAGE_KEY)
  }
}

/**
 * Check if currently in backoff period
 */
export function isInBackoff(): boolean {
  const state = getBackoffState()

  // If backoff expired, clear it
  if (state.backoffUntil !== null && !state.isInBackoff && failureCount > 0) {
    clearBackoffState()
  }

  return state.isInBackoff
}

/**
 * Manually clear backoff state - useful for testing/debugging
 */
export function clearBackoffState(): void {
  failureCount = 0
  successCount = 0
  removeStorageItem(STORAGE_KEY)
}

/**
 * Get configuration constants (useful for testing)
 */
export function getConfig(): {
  BACKOFF_DURATION_MS: number
  FAILURE_THRESHOLD: number
  SUCCESS_THRESHOLD: number
  STORAGE_KEY: string
} {
  return {
    BACKOFF_DURATION_MS,
    FAILURE_THRESHOLD,
    SUCCESS_THRESHOLD,
    STORAGE_KEY,
  }
}
